import React, { useState, useEffect } from 'react';
import { Layers, Calendar, Wrench, PackageCheck, Building2 } from 'lucide-react';

import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import { fetchStoresWhereStaffAssigned } from '../apiHelper';
import { useToast } from '../hooks/useToast';

import { ProductSpecsManager } from './ProductSpecsManager';
import { ProductionOrderScheduler } from './ProductionOrderScheduler';
import { ProductionFloorConsole } from './ProductionFloorConsole'
import { MaterialConsumptionStation } from './MaterialConsumptionStation';

const TABS = [
    { id: 'specs', label: 'Engineering Blueprints', icon: Layers },
    { id: 'scheduler', label: 'Order Scheduling', icon: Calendar },
    { id: 'kitting', label: 'Material Kitting', icon: PackageCheck },
    { id: 'operator', label: 'Workstation Terminal', icon: Wrench },
];

const ManufacturingDashboard = () => {
    const { showToast } = useToast();
    const [user, setUser] = useState(null);
    const [selectedStore, setSelectedStore] = useState('');
    const [storeOptions, setStoreOptions] = useState([]);
    const [activeTab, setActiveTab] = useState('specs');

    useEffect(() => {
        const init = async () => {
            const stored = localStorage.getItem('user');
            const parsed = stored ? JSON.parse(stored) : null;
            setUser(parsed);

            const stores = await fetchStoresWhereStaffAssigned(parsed);
            if (stores && stores.length > 0) {
                const filteredStores = stores.filter(store => store.storetype !== 'WHS')
                const mapped = filteredStores.map(s => ({
                    label: s.identifier || `Branch ${s.store_id}`,
                    value: s.store_id.toString()
                }));
                
                setStoreOptions(mapped);
                setSelectedStore(mapped[0].value);
            } else {
                showToast("No store locations assigned.", "error");
            }
        };
        init();
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
            {/* Plant Context Selector Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Manufacturing & Resource Operations</h1>
                    <p className="text-xs text-zinc-500 mt-1">Role-based shop floor operations, material kitting, and specification manager.</p>
                </div>
                <div className="w-full md:w-64">
                    <SearchableSelectBox
                        options={storeOptions}
                        value={selectedStore}
                        onChange={(e) => setSelectedStore(e.target.value)}
                        placeholder="Select operations plant..."
                    />
                </div>
            </div>

            {/* Modular Role Navigation */}
            <div className="flex border-b border-zinc-200 space-x-2">
                {TABS.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
                                isActive
                                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                    : 'border-transparent text-zinc-500 hover:text-zinc-800 hover:border-zinc-300'
                            }`}
                        >
                            <Icon size={16} />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Active View Execution Context */}
            <div className="pt-2">
                {activeTab === 'specs' && <ProductSpecsManager storeId={selectedStore} currentUser={user} />}
                {activeTab === 'scheduler' && <ProductionOrderScheduler storeId={selectedStore} currentUser={user} />}
                {activeTab === 'kitting' && <MaterialConsumptionStation storeId={selectedStore} currentUser={user} />}
                {activeTab === 'operator' && <ProductionFloorConsole storeId={selectedStore} />}
            </div>
        </div>
    );
};

export default ManufacturingDashboard;